import { useEffect, useState } from "react";
import {
  alpha,
  Box,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import moment from "moment";
import { getData } from "../../../components/utilities/UtilityFunctions";
import { getEnvironmentVariable } from "../../../global/environmentVariables";

const IS_DEMO = getEnvironmentVariable("IS_DEMO") === "true";
const HEADER_SOLFORGE_URL = getEnvironmentVariable("HEADER_SOLFORGE_URL");
const COMPANY_FLAG = getEnvironmentVariable("COMPANY_FLAG");
const SYSTEM_IMAGES_CLOUDFRONT = getEnvironmentVariable(
  "SYSTEM_IMAGES_CLOUDFRONT"
);
const SINGLE_GAME_SYSTEM = getEnvironmentVariable("SINGLE_GAME_SYSTEM");
const LABEL_GAMESYSTEM = getEnvironmentVariable("LABEL_GAMESYSTEM");

const useStyles = makeStyles((theme) => ({
  text: {
    fontSize: "0.85em",
    marginBottom: "unset",
    "& .highlighted": {
      fontWeight: "600",
    },
  },
  wrapper: {
    "& .highlighted": {
      backgroundColor:
        theme?.palette?.highlight?.main || theme?.palette?.warning?.main,
      color:
        theme?.palette?.highlight?.contrastText ||
        theme?.palette?.warning?.contrastText,
    },
  },
  privateWrapper: {
    backgroundColor: theme?.palette?.error?.main
      ? alpha(theme?.palette?.error?.main, 0.15)
      : null,
    "& .highlighted": {
      backgroundColor:
        theme?.palette?.highlight?.main || theme?.palette?.warning?.main,
      color:
        theme?.palette?.highlight?.contrastText ||
        theme?.palette?.warning?.contrastText,
    },
  },
}));

const fetchFile = async (fileId, size, imageCache) => {
  if (!fileId) return;

  if (imageCache) {
    if (!imageCache[size || 0]) {
      imageCache[size || 0] = {};
    }
    if (imageCache[size || 0][fileId]) {
      return imageCache[size || 0][fileId];
    }
  }

  var returnObj;
  try {
    returnObj = await getData(`/files/${fileId}`, {
      queryStringParameters: {
        size: size,
      },
    });

    if (imageCache) {
      imageCache[size || 0][fileId] = returnObj;
    }
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

const ImagePreview = (props) => {
  const { armyList, superDense, imageCache } = props;
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(null);
  useEffect(() => {
    const go = async () => {
      setLoading(true);
      let imageFileId =
        armyList?.imageFileId || armyList?.character?.imageFileId;
      if (!imageFileId) {
        setImage({
          url: `${SYSTEM_IMAGES_CLOUDFRONT}/images/assets/cardback.png`,
        });
        setLoading(false);
        return;
      }

      if (image) {
        if (image.id === imageFileId) {
          setLoading(false);
          return;
        }
        setImage(null);
      }

      try {
        var file = await fetchFile(imageFileId, "medium", imageCache);
        if (file) {
          setImage({
            id: imageFileId,
            ...file,
          });
        }
      } catch (err) {
        //
      }
      setLoading(false);
    };

    go();
    // eslint-disable-next-line
  }, [armyList]);

  if (!image?.url || loading) {
    return (
      <Box
        sx={{
          flex: `0 0 ${superDense ? "28.57px" : "106px"}`,
          maxWidth: superDense ? "28.57px" : "106px",
          minWidth: superDense ? "28.57px" : "106px",
          height: superDense ? "40px" : "135px",
          mt: "-5px !important",
          borderRadius: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {loading && <CircularProgress size={superDense ? 10 : 50} />}
      </Box>
    );
  }
  return (
    <img
      src={image.url}
      alt={armyList?.name}
      style={{
        flex: `0 0 ${superDense ? "28.57px" : "106px"}`,
        maxWidth: superDense ? "28.57px" : "106px",
        minWidth: superDense ? "28.57px" : "106px",
        height: superDense ? "40px" : "135px",
        mt: "-5px !important",
        borderRadius: 2,
      }}
    />
  );
};

export default function ArmyListCard(props) {
  const { armyList, imageCache } = props;

  const classes = useStyles();
  return (
    <Box
      sx={{
        boxSizing: "border-box",
        width: "100%",
        maxWidth: "100%",

        mt: 1,
      }}
    >
      <Box
        className={
          armyList && !armyList?.public
            ? classes.privateWrapper
            : classes.wrapper
        }
        sx={{
          flexFlow: "row wrap",
          boxSizing: "border-box",
          dispay: "flex",
        }}
      >
        {armyList && (
          <Link
            href={
              armyList?.armyListSolforge?.id
                ? `${
                    HEADER_SOLFORGE_URL || ""
                  }/fused/${armyList.armyListSolforge.id}`
                : COMPANY_FLAG === "jasco" && armyList.isTextList
                  ? `/legacy/list/${armyList.id}`
                  : `/${
                      COMPANY_FLAG === "shard-bugs" ||
                      COMPANY_FLAG === "jasco" ||
                      IS_DEMO
                        ? "deck"
                        : "list"
                    }/${armyList?.id}`
            }
            color="inherit"
            underline="none"
            sx={{
              flexDirection: "row",
              boxSizing: "border-box",
              display: "flex",
              cursor: "pointer",
            }}
          >
            <ImagePreview armyList={armyList} imageCache={imageCache} />
            <Box
              sx={{
                flex: "1 1 0%",
                boxSizing: "border-box",
                ml: 1,
                mr: "2px",
                height: "100%",
                maxHeight: "135px",
                display: "flex",
                flexFlow: "column",
              }}
            >
              <Box
                sx={{
                  mt: "5px",
                  mb: "5px",
                  flex: "0 1 auto",
                }}
              >
                <Grid container alignItems="center">
                  {COMPANY_FLAG === "jasco" &&
                    (armyList.army?.name || armyList.player?.armyName) && (
                      <Grid item sx={{ mr: 1 }}>
                        <img
                          alt={armyList.army?.name || armyList.player?.armyName}
                          src={`${SYSTEM_IMAGES_CLOUDFRONT}/images/assets/${
                            armyList.army?.name || armyList.player?.armyName
                          }.png`}
                          style={{
                            maxHeight: "24px",
                          }}
                        />
                      </Grid>
                    )}
                  <Grid item>
                    <Typography gutterBottom className={classes.text}>
                      {armyList?.name ||
                        (armyList?.user?.firstName || armyList?.user?.lastName
                          ? `${armyList?.user?.firstName || ""} ${
                              armyList?.user?.lastName || ""
                            }`.trim()
                          : `${armyList?.player?.firstName || ""} ${
                              armyList?.player?.lastName || ""
                            }`.trim())}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ borderColor: "gray.main" }} />
              </Box>
              <Box
                sx={{
                  flex: "1 1 auto",
                  overflow: "auto",
                  mb: "5px",
                }}
              >
                <Typography gutterBottom className={classes.text}>
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "0.75rem",
                      lineHeight: "1.66",
                    }}
                  >
                    By:
                  </span>{" "}
                  {`${armyList?.user?.firstName || ""} ${
                    armyList?.user?.lastName || ""
                  }`.trim()}
                </Typography>

                <Typography gutterBottom className={classes.text}>
                  <span
                    style={{
                      fontWeight: "600",
                      fontSize: "0.75rem",
                      lineHeight: "1.66",
                    }}
                  >
                    Created:
                  </span>{" "}
                  {moment(armyList?.updated_at).format("MMM D, YYYY")}
                </Typography>

                {!SINGLE_GAME_SYSTEM && armyList.gameSystem?.name && (
                  <Typography gutterBottom className={classes.text}>
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: "0.75rem",
                        lineHeight: "1.66",
                      }}
                    >
                      {LABEL_GAMESYSTEM}:
                    </span>{" "}
                    {armyList.gameSystem?.name}
                  </Typography>
                )}

                {armyList?.deckFormat && (
                  <Typography gutterBottom className={classes.text}>
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: "0.75rem",
                        lineHeight: "1.66",
                      }}
                    >
                      Format:
                    </span>{" "}
                    {armyList?.deckFormat}
                  </Typography>
                )}
                {armyList?.subFaction?.name && (
                  <Typography gutterBottom className={classes.text}>
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: "0.75rem",
                        lineHeight: "1.66",
                      }}
                    >
                      {armyList?.gameSystem?.subFactionLabel || "Sub Faction"}:
                    </span>{" "}
                    {armyList?.subFaction?.name}
                  </Typography>
                )}
                {armyList?.character?.name && (
                  <Typography gutterBottom className={classes.text}>
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: "0.75rem",
                        lineHeight: "1.66",
                      }}
                    >
                      Character:
                    </span>{" "}
                    {armyList?.character?.name}
                  </Typography>
                )}
              </Box>
            </Box>
          </Link>
        )}
      </Box>
    </Box>
  );
}
